// src/pdf-calcs/d-rating-calculations.js
// D-Rating calculation logic for all variants. No DOM or jQuery dependencies.
//
// Standards:
//   - Prime Mover FW, A-Trailer FW, Dolly FW: AS 4968.1:2023
//   - Dolly Drawbar Coupling: AS 2213.1:2022
//
// All inputs in tonnes (t). Output D-rating in kilonewtons (kN).

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Validate that all required keys are present and numeric.
 * Returns { ok: true } or { ok: false, error: string }.
 */
function validateInputs(input, requiredKeys) {
   for (const key of requiredKeys) {
      const val = input[key];
      if (val === undefined || val === null || val === '') {
         return { ok: false, error: `Invalid input: ${key} is required.` };
      }
      if (typeof val !== 'number' || !Number.isFinite(val)) {
         return { ok: false, error: `Invalid input: ${key} must be a finite number.` };
      }
   }
   return { ok: true };
}

/** Build a success result. rawD is the unrounded kN value. */
function successResult(rawD, inputs, intermediate, roundFn = Math.ceil) {
   if (!Number.isFinite(rawD)) {
      return failResult('Calculation produced a non-finite result.', inputs);
   }
   if (rawD < 0) {
      return failResult('Calculation produced a negative D-rating.', inputs);
   }
   return {
      ok: true,
      rawD,
      roundedD: roundFn(rawD),
      inputs,
      intermediate,
   };
}

/** Build a failure result. */
function failResult(error, inputs) {
   return { ok: false, error, inputs };
}

// ---------------------------------------------------------------------------
// 1. Prime Mover Fifth Wheel (AS 4968.1:2023)
// ---------------------------------------------------------------------------

const PM_SINGLE_KEYS = ['steerAxleMass', 'driveAxleMass', 'trailerAxleMass', 'primeMoverTare'];

export function primeMoverSingle(input) {
   const v = validateInputs(input, PM_SINGLE_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const { steerAxleMass, driveAxleMass, trailerAxleMass, primeMoverTare } = input;

   const front = steerAxleMass + driveAxleMass;
   const rear = trailerAxleMass + (front - primeMoverTare);

   const divisor = front + trailerAxleMass;
   if (divisor <= 0) return failResult('Invalid divisor: front + trailerAxleMass must be positive.', input);

   const rawD = (5.886 * front * rear) / divisor;

   return successResult(rawD, input, { front, rear });
}

const PM_MULTI_KEYS = ['totalCombinationMass', 'rearAxleBehind', 'steerAxleMass', 'driveAxleMass', 'primeMoverTare'];

export function primeMoverMulti(input) {
   const v = validateInputs(input, PM_MULTI_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const { totalCombinationMass, rearAxleBehind, steerAxleMass, driveAxleMass, primeMoverTare } = input;

   if (totalCombinationMass <= 0) return failResult('Invalid divisor: totalCombinationMass must be positive.', input);

   const front = steerAxleMass + driveAxleMass;
   const adjustedRear = rearAxleBehind + (front - primeMoverTare);

   const rawD = (4.905 * adjustedRear * (front + 0.08 * adjustedRear)) / totalCombinationMass;

   return successResult(rawD, input, { front, adjustedRear });
}

// ---------------------------------------------------------------------------
// 2. A-Trailer Fifth Wheel (AS 4968.1:2023)
// ---------------------------------------------------------------------------

const AT_BDOUBLE_KEYS = [
   'totalCombinationMass',
   'primeMoverTare',
   'trailerTare',
   'rearAxleBehind',
   'trailerAxleMass',
   'trailerPayload',
   'frontAxleAhead',
];

function aTrailerABCore(input, requiredKeys, computeAB) {
   const v = validateInputs(input, requiredKeys);
   if (!v.ok) return failResult(v.error, input);

   const { totalCombinationMass } = input;
   if (totalCombinationMass <= 0) {
      return failResult('Invalid divisor: totalCombinationMass must be positive.', input);
   }

   const { A, B } = computeAB(input);
   const rawD = (4.905 * A * B) / totalCombinationMass;

   return successResult(rawD, input, { A, B });
}

export function aTrailerBDouble(input) {
   return aTrailerABCore(
      input,
      AT_BDOUBLE_KEYS,
      ({ primeMoverTare, trailerTare, rearAxleBehind, trailerAxleMass, trailerPayload, frontAxleAhead }) => {
         const A = rearAxleBehind + (frontAxleAhead + trailerAxleMass - trailerPayload - trailerTare - primeMoverTare);
         const B = frontAxleAhead + trailerAxleMass + 0.08 * A;
         return { A, B };
      },
   );
}

// Same formula as aTrailerBDouble; named separately in case of future divergence.
export function aTrailerBTripleSecond(input) {
   return aTrailerBDouble(input);
}

const AT_BTRIPLE_THIRD_KEYS = [
   'totalCombinationMass',
   'primeMoverTare',
   'leadTrailerTare',
   'leadTrailerPayload',
   'rearAxleBehind',
   'leadTrailerAxleMass',
   'secondTrailerTare',
   'secondTrailerPayload',
   'secondTrailerAxleMass',
   'frontAxleAhead',
];

export function aTrailerBTripleThird(input) {
   return aTrailerABCore(
      input,
      AT_BTRIPLE_THIRD_KEYS,
      ({
         primeMoverTare,
         leadTrailerTare,
         leadTrailerPayload,
         rearAxleBehind,
         leadTrailerAxleMass,
         secondTrailerTare,
         secondTrailerPayload,
         secondTrailerAxleMass,
         frontAxleAhead,
      }) => {
         const A =
            rearAxleBehind +
            (frontAxleAhead +
               leadTrailerAxleMass +
               secondTrailerAxleMass -
               leadTrailerPayload -
               leadTrailerTare -
               primeMoverTare -
               secondTrailerPayload -
               secondTrailerTare);
         const B = frontAxleAhead + leadTrailerAxleMass + secondTrailerAxleMass + 0.08 * A;
         return { A, B };
      },
   );
}

// ---------------------------------------------------------------------------
// 3. Dolly Fifth Wheel (AS 4968.1:2023)
// ---------------------------------------------------------------------------

const DOLLY_HINGED_KEYS = ['totalCombinationMass', 'dollyTare', 'rearAxleBehind', 'frontAxleAhead', 'dollyAxleMass'];

export function dollyHinged(input) {
   const v = validateInputs(input, DOLLY_HINGED_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const { totalCombinationMass, dollyTare, rearAxleBehind, frontAxleAhead, dollyAxleMass } = input;

   const transferred = dollyAxleMass - dollyTare;
   const rear = rearAxleBehind + transferred;
   const front = frontAxleAhead + dollyTare;

   const divisor = totalCombinationMass - transferred;
   if (divisor <= 0) return failResult('Invalid divisor: totalCombinationMass - transferred must be positive.', input);

   const rawD = (4.905 * rear * (front + 0.08 * rear)) / divisor;

   return successResult(rawD, input, { transferred, front, rear });
}

// Same formula; named separately in case of future divergence.
export const dollyHingedSecond = dollyHinged;

const DOLLY_RIGID_KEYS = [
   'totalCombinationMass',
   'dollyTare',
   'rearAxleBehind',
   'frontAxleAhead',
   'dollyAxleMass',
   'transferMass',
];

export function dollyRigid(input) {
   const v = validateInputs(input, DOLLY_RIGID_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const { totalCombinationMass, dollyTare, rearAxleBehind, frontAxleAhead, dollyAxleMass, transferMass } = input;

   const adjustedTare = dollyTare - transferMass;
   const transferred = dollyAxleMass - adjustedTare;
   const rear = rearAxleBehind + transferred;
   const front = frontAxleAhead + adjustedTare;

   const divisor = totalCombinationMass - transferred;
   if (divisor <= 0) return failResult('Invalid divisor: totalCombinationMass - transferred must be positive.', input);

   const rawD = (4.905 * rear * (front + 0.08 * rear)) / divisor;

   return successResult(rawD, input, { adjustedTare, transferred, front, rear });
}

// Same formula; named separately in case of future divergence.
export const dollyRigidSecond = dollyRigid;

// ---------------------------------------------------------------------------
// 4. Dolly Drawbar Coupling (AS 2213.1:2022)
// ---------------------------------------------------------------------------

const DRAWBAR_KEYS = ['frontMass', 'rearMass', 'factor'];

export function calculateDollyDrawbar(input) {
   const v = validateInputs(input, DRAWBAR_KEYS);
   if (!v.ok) return failResult(v.error, input);

   const { frontMass, rearMass, factor } = input;

   if (frontMass <= 0) return failResult('Invalid input: frontMass must be greater than zero.', input);
   if (rearMass <= 0) return failResult('Invalid input: rearMass must be greater than zero.', input);
   if (factor <= 0) return failResult('Invalid input: factor must be greater than zero.', input);

   const divisor = frontMass + rearMass;
   const rawD = (factor * 9.81 * frontMass * rearMass) / divisor;

   return successResult(rawD, input, { factor });
}

export function dollyDrawbarRigidSingle(input) {
   return calculateDollyDrawbar({ ...input, factor: 1.0 });
}

export function dollyDrawbarRigidDoubleFirst(input) {
   return calculateDollyDrawbar({ ...input, factor: 0.9 });
}

export function dollyDrawbarRigidDoubleSecond(input) {
   return calculateDollyDrawbar({ ...input, factor: 0.9 });
}

export function dollyDrawbarRoadTrainSingle(input) {
   return calculateDollyDrawbar({ ...input, factor: 0.65 });
}

export function dollyDrawbarRoadTrainDoubleFirst(input) {
   return calculateDollyDrawbar({ ...input, factor: 0.6 });
}

export function dollyDrawbarRoadTrainDoubleSecond(input) {
   return calculateDollyDrawbar({ ...input, factor: 0.6 });
}

// ---------------------------------------------------------------------------
// Calculator registry
// ---------------------------------------------------------------------------

export const dRatingCalculators = {
   primeMoverSingle,
   primeMoverMulti,
   aTrailerBDouble,
   aTrailerBTripleSecond,
   aTrailerBTripleThird,
   dollyHinged,
   dollyHingedSecond,
   dollyRigid,
   dollyRigidSecond,
   dollyDrawbarRigidSingle,
   dollyDrawbarRigidDoubleFirst,
   dollyDrawbarRigidDoubleSecond,
   dollyDrawbarRoadTrainSingle,
   dollyDrawbarRoadTrainDoubleFirst,
   dollyDrawbarRoadTrainDoubleSecond,
};
